import React from 'react'
import { Box } from '@mui/material'

const VIEWBOX = 100
const CENTER = 50

const DEFAULT_CLASSES = {
  root: 'CircularProgressbar',
  trail: 'CircularProgressbar-trail',
  path: 'CircularProgressbar-path',
  text: 'CircularProgressbar-text',
  background: 'CircularProgressbar-background',
}

function clamp(n, min, max) {
  if (!Number.isFinite(n)) return min
  return Math.min(max, Math.max(min, n))
}

export function getPathRatio(value, minValue, maxValue) {
  const min = Number.isFinite(minValue) ? minValue : 0
  const max = Number.isFinite(maxValue) ? maxValue : 100
  if (max === min) return 0
  return clamp((Number(value) - min) / (max - min), 0, 1)
}

function getPathDescription(pathRadius, counterClockwise) {
  const rotation = counterClockwise ? 1 : 0
  return `
      M ${CENTER},${CENTER}
      m 0,-${pathRadius}
      a ${pathRadius},${pathRadius} ${rotation} 1 1 0,${2 * pathRadius}
      a ${pathRadius},${pathRadius} ${rotation} 1 1 0,-${2 * pathRadius}
    `
}

function getDashStyle(pathRadius, dashRatio, counterClockwise) {
  const diameter = Math.PI * 2 * pathRadius
  const gapLength = (1 - dashRatio) * diameter
  return {
    strokeDasharray: `${diameter}px ${diameter}px`,
    strokeDashoffset: `${counterClockwise ? -gapLength : gapLength}px`,
  }
}

function ProgressPath({
  className,
  counterClockwise,
  dashRatio,
  pathRadius,
  strokeWidth,
  style,
}) {
  return (
    <path
      className={className}
      style={{
        ...style,
        ...getDashStyle(pathRadius, dashRatio, counterClockwise),
      }}
      d={getPathDescription(pathRadius, counterClockwise)}
      strokeWidth={strokeWidth}
      fillOpacity={0}
    />
  )
}

/**
 * Drop-in for `react-circular-progressbar`.
 * Keeps `.CircularProgressbar*` class names for existing SCSS.
 */
export function CircularProgressbar({
  value = 0,
  minValue = 0,
  maxValue = 100,
  text,
  strokeWidth = 8,
  className = '',
  classes = DEFAULT_CLASSES,
  styles = {},
  background = false,
  backgroundPadding = 0,
  circleRatio = 1,
  counterClockwise = false,
  sx,
}) {
  const padding = background ? backgroundPadding : 0
  const pathRadius = CENTER - Number(strokeWidth) / 2 - padding
  const pathRatio = getPathRatio(value, minValue, maxValue)
  const rootClass = `${classes.root || DEFAULT_CLASSES.root} ${className}`.trim()
  const sxList = [
    { width: 90 },
    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
  ]

  return (
    <Box
      component="svg"
      className={rootClass}
      style={styles.root}
      sx={sxList}
      viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
      data-test-id="CircularProgressbar"
    >
      {background ? (
        <circle
          className={classes.background || DEFAULT_CLASSES.background}
          style={styles.background}
          cx={CENTER}
          cy={CENTER}
          r={VIEWBOX / 2}
        />
      ) : null}
      <ProgressPath
        className={classes.trail || DEFAULT_CLASSES.trail}
        counterClockwise={counterClockwise}
        dashRatio={circleRatio}
        pathRadius={pathRadius}
        strokeWidth={strokeWidth}
        style={styles.trail}
      />
      <ProgressPath
        className={classes.path || DEFAULT_CLASSES.path}
        counterClockwise={counterClockwise}
        dashRatio={pathRatio * circleRatio}
        pathRadius={pathRadius}
        strokeWidth={strokeWidth}
        style={styles.path}
      />
      {text ? (
        <text
          className={classes.text || DEFAULT_CLASSES.text}
          style={styles.text}
          x={CENTER}
          y={CENTER}
        >
          {text}
        </text>
      ) : null}
    </Box>
  )
}

export default CircularProgressbar
